import { Article } from '../../types/strapi';

export interface StrapiProps {
  endpoint: string;
  query?: Record<string, string>;
  wrappedByKey?: string;
  wrappedByList?: boolean;
}

export async function fetchApi<T>({
  endpoint,
  query,
  wrappedByKey,
  wrappedByList,
}: StrapiProps): Promise<T | undefined> {
  try {
    if (endpoint.startsWith('/')) {
      endpoint = endpoint.slice(1);
    }

    const url = new URL(`${process.env.STRAPI_URL}/api/${endpoint}`);

    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }

    const res = await fetch(url.toString(), {
      headers: {
        Authorization: `Bearer ${process.env.STRAPI_TOKEN}`,
      },
    });

    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }

    let data = await res.json();

    if (wrappedByKey) {
      data = data[wrappedByKey];
    }

    if (wrappedByList) {
      data = data[0];
    }

    return data as T;
  } catch (error) {
    console.error('Error fetching API:', error);
  }
}

export interface GetAllArticlesByCategoryProps {
  category: string | Array<string> | null;
  subscriberOnly?: boolean;
  projectTypes?: string[];
}

export const getAllArticlesByCategory = async (config: GetAllArticlesByCategoryProps) => {
  const { category, subscriberOnly, projectTypes } = config;

  const query: Record<string, string> = {
    'populate[0]': 'cover',
    'populate[1]': 'post_category',
    'populate[2]': 'project_types',
    'populate[3]': 'author',
    fields: '*',
    'sort[0]': 'published_date:desc',
    'pagination[pageSize]': '500',
    'filters[visibility][$ne]': 'public_only',
    status: process.env.IS_PRODUCTION_APP === 'true' ? 'published' : 'draft',
  };

  if (category !== null && !subscriberOnly) {
    if (Array.isArray(category)) {
      category.forEach((cat, i) => {
        query[`filters[$or][${i}][post_category][url_slug][$eqi]`] = cat;
      });
    } else {
      query['filters[post_category][url_slug][$eqi]'] = category;
    }
  }

  if (subscriberOnly) {
    query['filters[visibility][$eq]'] = 'subscriber_only';
  }
  if (projectTypes && projectTypes.length > 0) {
    delete query['filters[visibility][$ne]'];
    query['filters[$or][0][visibility][$ne]'] = 'subscriber_only';
    query['filters[$or][1][$and][0][visibility][$eq]'] = 'subscriber_only';
    query['filters[$or][1][$and][1][$or][0][project_types][$null]'] = 'true';
    projectTypes.forEach((type, i) => {
      query[`filters[$or][1][$and][1][$or][1][project_types][name][$in][${i}]`] = type;
    });
  }

  const articles = await fetchApi<Article[]>({
    endpoint: 'articles',
    query,
    wrappedByKey: 'data',
  });

  return articles || [];
};
